import styled from '@emotion/styled';
import { Modal, Table } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { FlexWrapper } from '../../theme/common_style';
import { CustomCheckbox } from '../hms/HmsFilter';

const HandleColumnValue = ({ data, type }) => {
  return (
    <FlexWrapper justify="center" align="center">
      <CustomCheckbox
        checked={data?.[type]}
        disabled
        bgColor={data?.changed ? (type === 'oldValue' ? 'red' : 'green') : false}
      />
    </FlexWrapper>
  );
};

HandleColumnValue.propTypes = {
  data: PropTypes.shape({
    oldValue: PropTypes.bool,
    newValue: PropTypes.bool,
    changed: PropTypes.bool
  }),
  type: PropTypes.oneOf(['oldValue', 'newValue'])
};

const ChangedText = styled.span`
  color: ${({ changed, type }) => (changed ? (type === 'old' ? 'red' : 'green') : '#000')};
  font-weight: ${({ changed }) => (changed ? 500 : 400)};
  word-break: break-word;
`;

const ViewRoleActivity = ({ open, onCancel, data }) => {
  const [tableData, setTableData] = useState([]);

  const handleTextRender = (text, record, type) => {
    return (
      <ChangedText changed={record?.changed} type={type}>
        {text || 'N/A'}
      </ChangedText>
    );
  };

  const hasPermissionChanged = (record) =>
    ['read', 'create', 'update', 'delete'].some((key) => record[key]?.changed);

  const transformPermissions = (oldPermissions = [], newPermissions = []) => {
    const modules = Array.from(
      new Set([...oldPermissions, ...newPermissions].map((item) => item?.module))
    );

    return modules.map((module) => {
      const old = oldPermissions.find((item) => item?.module === module) || {};
      const current = newPermissions.find((item) => item?.module === module) || {};

      return {
        field: module,
        read: {
          oldValue: old.read || false,
          newValue: current.read || false,
          changed: old.read !== current.read
        },
        create: {
          oldValue: old.create || false,
          newValue: current.create || false,
          changed: old.create !== current.create
        },
        update: {
          oldValue: old.update || false,
          newValue: current.update || false,
          changed: old.update !== current.update
        },
        delete: {
          oldValue: old.del || false,
          newValue: current.del || false,
          changed: old.del !== current.del
        }
      };
    });
  };

  useEffect(() => {
    if (data) {
      setTableData(transformPermissions(data?.oldValue?.permissions, data?.newValue?.permissions));
    }
  }, [data]);

  const permissionColumns = useMemo(() => {
    const actions = ['read', 'create', 'update', 'delete'];

    const generateSubColumns = (action) => [
      {
        title: 'Old',
        align: 'center',
        dataIndex: action,
        render: (text) => <HandleColumnValue data={text} type="oldValue" />
      },
      {
        title: 'New',
        align: 'center',
        dataIndex: action,
        render: (text) => <HandleColumnValue data={text} type="newValue" />
      }
    ];

    return [
      {
        title: 'Field',
        dataIndex: 'field',
        key: 'field',
        render: (text) => <div style={{ fontWeight: 500 }}>{text}</div>
      },
      ...actions.map((action) => ({
        title: action.charAt(0).toUpperCase() + action.slice(1),
        children: generateSubColumns(action)
      }))
    ];
  }, []);

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      centered
      width="90%"
      footer={null}
      destroyOnClose
      prefixCls="view-activity-modal">
      <StyledContainer>
        <h2>Activity Log</h2>

        <FlexWrapper justify="space-between" align="center">
          <div className="activity-log">
            <span className="bold">{data?.action_type}d by</span>
            <span>{data?.user}</span>
            <span className="bold">on</span>
            <span>
              {data?.date}, {data?.time}
            </span>
          </div>
        </FlexWrapper>

        <Table
          prefixCls="activity-role-table"
          columns={[
            {
              title: 'Field',
              dataIndex: 'role',
              key: 'role',
              render: (text) => <div style={{ fontWeight: 500 }}>{text}</div>
            },
            {
              title: 'Old',
              dataIndex: 'oldValue',
              key: 'oldValue',
              render: (text, record) => handleTextRender(text, record, 'old')
            },
            {
              title: 'New',
              dataIndex: 'newValue',
              key: 'newValue',
              render: (text, record) => handleTextRender(text, record, 'new')
            }
          ]}
          dataSource={[
            {
              role: 'Role',
              oldValue: data?.oldValue?.role,
              newValue: data?.newValue?.role
            }
          ]}
          pagination={false}
          size="middle"
          bordered
        />

        <br />
        <strong>Permissions</strong>
        <Table
          prefixCls="activity-log-table"
          columns={permissionColumns}
          dataSource={tableData}
          pagination={false}
          size="middle"
          bordered
          rowKey="field"
          rowClassName={(record) =>
            data?.action_type === 'Update' && hasPermissionChanged(record) ? 'row-changed' : ''
          }
        />
      </StyledContainer>
    </Modal>
  );
};

ViewRoleActivity.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  data: PropTypes.shape({
    oldValue: PropTypes.object,
    newValue: PropTypes.object,
    user: PropTypes.string,
    action_type: PropTypes.string,
    date: PropTypes.string,
    time: PropTypes.string
  })
};

const StyledContainer = styled.div`
  h2 {
    margin: 0;
    text-align: center;
  }

  strong {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 5px;
    display: block;
  }

  .activity-log {
    display: flex;
    gap: 5px;
    margin: 20px 0 10px;
    font-size: 15px;

    .bold {
      font-weight: 500;
    }
  }

  .row-changed {
    background-color: #f6ffed;
  }

  .ant-table-cell {
    padding: 10px 12px;
    font-size: 14px;
  }

  .ant-table-thead > tr > th {
    font-weight: 600;
    background-color: #fafafa;
  }
`;

export default ViewRoleActivity;
